import { type ReactNode } from 'react'
import { Button } from '@/components/Button'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
} from '@/components/ModalSheet'
import styles from './ManageSkillsListDialog.module.css'
import { SearchField } from '@/components/SearchField'
import { Checkbox } from '@/components/Checkbox'
import { ListItemDescription, ListItemTitle } from '@/components/ListItem'
import type { Id } from '@/infra/id'

export interface ManageSkillsListDialogSkill {
  id: Id
  name: string
  description: string
  tagLabels: string[]
  isSelected: boolean
  onToggle: () => void
}

export interface ManageSkillsListDialogProps {
  showing: boolean
  onClose: () => void
  onSearchTextChange: (value: string) => void
  isWorkspaceSelected: boolean
  allSelected: boolean
  onSelectAllChange: (checked: boolean) => void
  skills: ManageSkillsListDialogSkill[]
  scrollLoader: ReactNode
  onSubmit: () => void
  isSubmitPending: boolean
}

export function ManageSkillsListDialog({
  showing,
  onClose,
  onSearchTextChange,
  isWorkspaceSelected,
  allSelected,
  onSelectAllChange,
  skills,
  scrollLoader,
  onSubmit,
  isSubmitPending,
}: ManageSkillsListDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSubmitPending ? undefined : onClose}
      disableCloseOnOutsideClick
    >
      <ModalSheetHeader withCloseButton>
        <SearchField
          stretch
          embedded
          placeholder="Search skills"
          onChange={(value: string) => onSearchTextChange(value)}
          testId="search-field"
        />
      </ModalSheetHeader>
      {isWorkspaceSelected && (
        <ModalSheetContent>
          <ModalSheetSection spaced>
            <div>
              <table className={styles.editSkillTable}>
                <thead>
                  <tr>
                    <th align="left">Skill</th>
                    <th align="right">Tags</th>
                    <th align="right">
                      <Checkbox
                        checked={allSelected}
                        onChange={onSelectAllChange}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {skills.map((skill) => (
                    <tr key={skill.id} className={styles.editSkillItemRow}>
                      <td align="left">
                        <ListItemTitle>{skill.name}</ListItemTitle>
                        <ListItemDescription>
                          {skill.description}
                        </ListItemDescription>
                      </td>
                      <td align="right">
                        <div className={styles.tagColumn}>
                          <ListItemDescription>
                            {skill.tagLabels.join(', ')}
                          </ListItemDescription>
                        </div>
                      </td>
                      <td align="right">
                        <Checkbox
                          checked={skill.isSelected}
                          onChange={skill.onToggle}
                          testId={`edit-skills-row-checkbox-${skill.id}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {scrollLoader}
          </ModalSheetSection>
        </ModalSheetContent>
      )}

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            variant="default"
            onPress={onClose}
            isDisabled={isSubmitPending}
          />
          <Button
            label="Save"
            variant="primary"
            isDisabled={isSubmitPending}
            testId="edit-skills-submit"
            onPress={onSubmit}
            isPending={isSubmitPending}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
